import {AxiosInstance} from "axios";
import Bill from "../model/bill/Bill";
import DictionaryCache from "../helper/DictionaryCache";
import BillSummary from "../model/bill/BillSummary";
import {BillEventApiDto} from "./model/rest";
import {BillEventType, BillEventTypeEnum, default as BillEvent} from "../model/bill/event/BillEvent";
import BillUpdatedEvent from "../model/bill/event/BillUpdatedEvent";
import {option, Option} from "ts-option";

/**
 * Bill Api.
 */
export default class BillApi {
	/**
	 * Cache for searching bills.
	 * It prevents unnecessary request to the server.
	 */
	billsCache: DictionaryCache<BillSummary[]>;

	constructor(public readonly axiosInstance: AxiosInstance) {
		this.billsCache = DictionaryCache.empty();
	}

	/**
	 * Fetch bills for partner brand.
	 * @returns {Promise<Bill[]>} Promise containing all bills for partner brand.
	 */
	fetchBills(partnerBrandId: string): Promise<Bill[]> {
		const url = `/api/web/accounting/partner_brands/${partnerBrandId}/bills`;
		return this.axiosInstance.get(url).then(resp => resp.data.map(Bill.fromJson));
	}

	/**
	 * Fetch bill by its it.
	 * @param {string} billId
	 * @returns {Promise<Bill>}
	 */
	fetchBill(billId: string): Promise<Bill> {
		const url = `/api/web/accounting/bills/${billId}`;
		return this.axiosInstance.get(url).then(resp => Bill.fromJson(resp.data));
	}

	/**
	 * Fetch bill by job it.
	 * @returns {Promise<Bill>}
	 * @param jobId
	 */
	fetchBillByJobId(jobId: string): Promise<Bill> {
		const url = `/api/web/accounting/jobs/${jobId}/bill`;
		console.warn(`url request turned off:  /api/web/accounting/jobs/${jobId}/bill `)
		return Promise.reject()// this.axiosInstance.get(url).then(resp => Bill.fromJson(resp.data));
	}

	/**
	 * Fetch events for bill.
	 * @param {string} billId
	 * @returns {Promise<Array<BillEventType>>}
	 */
	fetchBillEvents(billId: string): Promise<Array<BillEventType>> {
		const url = `/api/web/accounting/bills/${billId}/events`;
		return this.axiosInstance.get(url).then(resp => resp.data.map((event: BillEventApiDto) => {
			switch (event.type) {
				case BillEventTypeEnum.BillPaid:
				case BillEventTypeEnum.BillCreated:
				case BillEventTypeEnum.NewBillInXeroCreated:
					return BillEvent.fromJson(event);
				case BillEventTypeEnum.BillUpdated:
					return BillUpdatedEvent.fromJson(event);
				default:
					//fallback
					return null;
			}
		}).filter((item: BillEvent) => item !== null));
	}

	/**
	 * Confirm full bill payment.
	 * @returns {Promise<any>}
	 * @param billId
	 */
	confirmFullBillPayment(billId: string): Promise<any> {
		const url = `/api/web/accounting/bills/${billId}/confirm_full_payment`;
		return this.axiosInstance.put(url);
	}

	/**
	 * Find bills by text.
	 * @param text text using to search.
	 * @returns promise with results.
	 */
	findBillsByText(text: string): Promise<BillSummary[]> {
		return new Promise((resolve, reject) => {
			const cachedData = this.billsCache.get(text);
			if (cachedData.isDefined)
				resolve(cachedData.get);
			else {
				const url = `/api/web/accounting/bills/search`;
				this.axiosInstance.get(url, {params: {text}}).then(resp => {
					const data = resp.data.map(BillSummary.fromJson);
					this.billsCache.store(text, data);
					resolve(data);
				}).catch(reject);
			}
		});
	}

	/**
	 * Change pay via mobile flag.
	 * @param billId
	 * @param payViaMobile
	 */
	changePayViaMobile(billId: string, payViaMobile: boolean) {
		const url = `/api/web/accounting/bills/${billId}/pay_via_mobile`;
		return this.axiosInstance.put(url, {payViaMobile}).then(resp => Bill.fromJson(resp.data));
	}

	/**
	 * Change pay via mobile flag.
	 * @returns {AxiosPromise<any>}
	 * @param bill
	 */
	updateBill(bill: Bill) {
		const url = `/api/web/accounting/bills/${bill.id}`;
		return this.axiosInstance.put(url, bill.toJson()).then(resp => Bill.fromJson(resp.data));
	}

	/**
	 * Send bill to installer.
	 * @param {string} billId
	 * @returns {AxiosPromise<any>}
	 */
	sendBillToInstaller(billId: string) {
		const url = `/api/web/accounting/bills/${billId}/email`;
		return this.axiosInstance.put(url);
	}

	/**
	 * Clear bills search cache.
	 */
	clearBillsCache() {
		this.billsCache = DictionaryCache.empty();
	}

	/**
	 * Get payment link for bill
	 * @param billId
	 */
	getPaymentLink(billId: string): Promise<string> {
		const url = `/api/web/accounting/bills/${billId}/payment_link`;
		return this.axiosInstance.get(url).then(resp => resp.data.url);
	}

	/**
	 * Mark bill as paid
	 * @param billId
	 */
	markBillAsPaid(billId: string) {
		const url = `/api/web/accounting/bills/${billId}/mark_as_paid`
		return this.axiosInstance.put(url)
	}

	/**
	 * Get Xero bill status
	 * @param billId
	 */
	getBillXeroStatus(billId: string): Promise<Option<string>> {
		const url = `/api/web/accounting/bills/${billId}/xero_invoice_status`;
		return this.axiosInstance.get(url).then(resp => option(resp.data.xeroInvoiceStatus));
	}
}
